import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableSkeleton, Amount, ListEmptyRow, StatCard } from '@redpanda/ui';
import { useRefetchOnFocus, useRememberedState } from '@redpanda/utils';
import { useProfitabilityReportCache } from '../stores/timeEntryStore';
import { useCrmCustomers, customerName } from '../stores/lookupStore';
import { useDataChanged } from '../utils/dataEvents';
import { ProfitabilityRow } from '../services/api';

type View = 'projects' | 'customers';
const VIEWS: View[] = ['projects', 'customers'];

/** 依客戶彙總（同一客戶的專案加總；沒關聯客戶的歸「未指定客戶」，排最後）；毛利只加有記錄成本的專案 */
function byCustomer(rows: ProfitabilityRow[]) {
  const map = new Map<string | null, { customerId: string | null; count: number; revenue: number; laborCost: number; expenseCost: number; totalCost: number; margin: number; costedRevenue: number }>();
  for (const row of rows) {
    const agg = map.get(row.customerId) ?? { customerId: row.customerId, count: 0, revenue: 0, laborCost: 0, expenseCost: 0, totalCost: 0, margin: 0, costedRevenue: 0 };
    agg.count++;
    agg.revenue += row.revenue;
    agg.laborCost += row.laborCost;
    agg.expenseCost += row.expenseCost;
    agg.totalCost += row.totalCost;
    if (row.totalCost > 0) {
      agg.margin += row.margin;
      agg.costedRevenue += row.revenue;
    }
    map.set(row.customerId, agg);
  }
  return Array.from(map.values()).sort((a, b) => (a.customerId === null ? 1 : 0) - (b.customerId === null ? 1 : 0) || b.margin - a.margin);
}

const rate = (margin: number, revenue: number) => (revenue > 0 ? Math.round((margin / revenue) * 1000) / 10 : null);

/**
 * 利潤報表（管理員）：跨專案比較毛利，看哪個客戶、哪類專案最賺錢
 * 毛利＝合約金額 −（人力成本＋外部成本）；還沒記任何成本的專案毛利率沒有意義，顯示「未記錄成本」且不列入整體毛利率
 */
const ProfitabilityPage: React.FC = () => {
  const { t } = useTranslation(['translation', 'pages']);
  const { data, refetch } = useProfitabilityReportCache('all');
  const customers = useCrmCustomers();
  const [view, setView] = useRememberedState<View>('pm-ui:profitability.view', 'projects', VIEWS);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refetch(); }, []);
  useDataChanged(['projects', 'packages', 'timeEntries', 'expenses'], refetch);
  useRefetchOnFocus(refetch);

  const tp = (k: string, options?: Record<string, unknown>) => t(`pmProfitability.${k}`, { ns: 'pages', ...options });
  const rows = data ?? [];
  const costed = rows.filter(r => r.totalCost > 0);
  const sum = (list: ProfitabilityRow[], pick: (r: ProfitabilityRow) => number) => list.reduce((s, r) => s + pick(r), 0);
  const overallRate = rate(sum(costed, r => r.margin), sum(costed, r => r.revenue));
  const overBudget = rows.filter(r => (r.costUsage ?? 0) >= 100).length;
  const filterClass = (active: boolean) =>
    `inline-flex shrink-0 items-center rounded px-3 py-1.5 text-sm transition-colors ${active ? 'bg-accent-soft text-accent font-medium' : 'text-fg-secondary hover:text-fg'}`;
  const rateCell = (value: number | null, hasCost: boolean) =>
    !hasCost ? <span className="text-[13px] text-fg-muted">{tp('noCost')}</span>
      : value === null ? <span className="text-fg-muted">—</span>
      : <span className={value < 0 ? 'text-red-600 dark:text-red-400' : ''}>{value}%</span>;
  const money = (value: number) => (value > 0 ? <Amount value={value} /> : <span className="text-fg-muted">—</span>);

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{tp('title')}</h1>
      </div>

      <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard title={tp('summary.revenue')} value={<Amount value={sum(rows, r => r.revenue)} />} hint={tp('summary.projects', { count: rows.length })} />
        <StatCard title={tp('summary.totalCost')} value={<Amount value={sum(rows, r => r.totalCost)} />} />
        <StatCard
          title={tp('summary.marginRate')}
          value={overallRate === null ? '—' : `${overallRate}%`}
          hint={tp('summary.costedProjects', { count: costed.length })}
        />
        <StatCard
          title={tp('summary.overBudget')}
          value={<span className={overBudget > 0 ? 'text-red-600 dark:text-red-400' : ''}>{overBudget}</span>}
        />
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <nav aria-label={tp('viewLabel')} className="inline-flex h-10 max-w-full items-center overflow-x-auto scrollbar-hide rounded-md border border-line bg-surface p-0.5">
          {VIEWS.map(v => (
            <button key={v} type="button" onClick={() => setView(v)} aria-pressed={view === v} className={filterClass(view === v)}>
              {tp(`view.${v}`)}
            </button>
          ))}
        </nav>
        <p className="min-w-0 flex-1 text-[13px] text-fg-secondary sm:text-right">{tp('hint')}</p>
      </div>

      {data === undefined ? (
        <TableSkeleton rows={6} columns={7} />
      ) : (
        <div className="bg-surface shadow dark:shadow-none dark:border dark:border-line overflow-hidden rounded-lg">
          <div className="overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <Table style={{ tableLayout: 'fixed', minWidth: '1100px' }}>
              <TableHeader>
                <TableRow>
                  <TableHead>{tp(view === 'projects' ? 'fields.project' : 'fields.customer')}</TableHead>
                  <TableHead numeric className="w-36">{tp('fields.revenue')}</TableHead>
                  <TableHead numeric className="w-32">{tp('fields.laborCost')}</TableHead>
                  <TableHead numeric className="w-32">{tp('fields.expenseCost')}</TableHead>
                  <TableHead numeric className="w-36">{tp('fields.margin')}</TableHead>
                  <TableHead numeric className="w-28">{tp('fields.marginRate')}</TableHead>
                  <TableHead numeric className="w-36">{view === 'projects' ? tp('fields.costUsage') : tp('fields.projectCount')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 ? (
                  <ListEmptyRow colSpan={7} filtered={false} title={tp('empty')} />
                ) : view === 'projects' ? (
                  rows.map(row => (
                    <TableRow key={row.projectId}>
                      <TableCell className="whitespace-normal">
                        <Link to={`/projects/${row.projectId}?tab=expenses`} className="font-medium text-fg hover:text-accent hover:underline">{row.name}</Link>
                        {row.customerId && <div className="mt-0.5 text-[13px] text-fg-secondary">{customerName(customers, row.customerId)}</div>}
                      </TableCell>
                      <TableCell numeric>{money(row.revenue)}</TableCell>
                      <TableCell numeric>{money(row.laborCost)}</TableCell>
                      <TableCell numeric>{money(row.expenseCost)}</TableCell>
                      <TableCell numeric className={`font-semibold ${row.margin < 0 ? 'text-red-600 dark:text-red-400' : ''}`}>
                        {row.totalCost > 0 ? <Amount value={row.margin} /> : <span className="font-normal text-fg-muted">—</span>}
                      </TableCell>
                      <TableCell numeric>{rateCell(row.marginRate, row.totalCost > 0)}</TableCell>
                      <TableCell numeric>
                        {row.costUsage === null ? <span className="text-fg-muted">—</span> : (
                          <span className={row.costUsage >= 100 ? 'text-red-600 dark:text-red-400' : row.costUsage >= 80 ? 'text-amber-700 dark:text-amber-400' : ''}>
                            {row.costUsage}%
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  byCustomer(rows).map(row => (
                    <TableRow key={row.customerId ?? 'unassigned'}>
                      <TableCell className="truncate">
                        {row.customerId ? customerName(customers, row.customerId) : <span className="text-fg-secondary">{tp('unassigned')}</span>}
                      </TableCell>
                      <TableCell numeric>{money(row.revenue)}</TableCell>
                      <TableCell numeric>{money(row.laborCost)}</TableCell>
                      <TableCell numeric>{money(row.expenseCost)}</TableCell>
                      <TableCell numeric className={`font-semibold ${row.margin < 0 ? 'text-red-600 dark:text-red-400' : ''}`}>
                        {row.totalCost > 0 ? <Amount value={row.margin} /> : <span className="font-normal text-fg-muted">—</span>}
                      </TableCell>
                      <TableCell numeric>{rateCell(rate(row.margin, row.costedRevenue), row.totalCost > 0)}</TableCell>
                      <TableCell numeric>{row.count}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfitabilityPage;
